import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmailTemplatesEditor from "./EmailTemplatesEditor";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async (overrides = null) => {
    setSaving(true);
    const payload = overrides ? { ...settings, ...overrides } : settings;
    try {
      const r = await api.put("/settings", payload);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="settings-page">
        <h1 className="font-serif text-3xl font-bold mb-2" style={{ color: "#292524" }}>Settings</h1>
        <p className="text-sm mb-8" style={{ color: "#57534E" }}>Portal-wide configuration</p>

        <div className="space-y-6 max-w-lg">
          {settings && (
            <>
            <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Invoice Reminders</p>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{ color: "#292524" }}>Automatic payment reminders</p>
                  <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>Send reminder emails for unpaid invoices</p>
                </div>
                <button
                  data-testid="reminder-toggle"
                  onClick={() => setSettings({ ...settings, reminder_enabled: !settings.reminder_enabled })}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                  style={{ background: settings.reminder_enabled ? "#D97706" : "#E7E5E4" }}
                >
                  <span
                    className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                    style={{ transform: settings.reminder_enabled ? "translateX(22px)" : "translateX(4px)" }}
                  />
                </button>
              </div>

              {settings.reminder_enabled && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                    Send reminder after (days overdue)
                  </Label>
                  <Input
                    data-testid="reminder-days-input"
                    type="number"
                    min="1"
                    max="90"
                    value={settings.reminder_days ?? 14}
                    onChange={(e) => setSettings({ ...settings, reminder_days: parseInt(e.target.value) || 14 })}
                    className="mt-1.5 w-32"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                  Sender Name
                </Label>
                <Input
                  data-testid="sender-name-input"
                  value={settings.sender_name ?? ""}
                  onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                  placeholder="Balbis"
                  className="mt-1.5"
                />
              </div>

              {/* Admin overdue invoice reminders */}
              <div className="border-t pt-5" style={{ borderColor: "#E7E5E4" }}>
                <p className="text-sm font-semibold mb-1" style={{ color: "#292524" }}>Admin overdue invoice alerts</p>
                <p className="text-xs mb-4" style={{ color: "#A8A29E" }}>Receive email reminders when invoices become overdue</p>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium" style={{ color: "#292524" }}>Enable overdue alerts</p>
                    <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>Email you when invoices are overdue</p>
                  </div>
                  <button
                    data-testid="admin-reminder-toggle"
                    onClick={() => setSettings({ ...settings, admin_overdue_reminder_enabled: !settings.admin_overdue_reminder_enabled })}
                    className="relative w-12 h-6 rounded-full transition-colors flex-shrink-0"
                    style={{ background: settings.admin_overdue_reminder_enabled ? "#D97706" : "#E7E5E4" }}
                  >
                    <span className="absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform"
                      style={{ transform: settings.admin_overdue_reminder_enabled ? "translateX(26px)" : "translateX(4px)" }} />
                  </button>
                </div>

                {settings.admin_overdue_reminder_enabled && (
                  <div className="space-y-3 pl-0">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>Alert email address</Label>
                      <Input
                        data-testid="admin-reminder-email-input"
                        type="email"
                        value={settings.admin_overdue_reminder_email ?? ""}
                        onChange={(e) => setSettings({ ...settings, admin_overdue_reminder_email: e.target.value })}
                        placeholder="you@balbis.studio"
                        className="mt-1.5"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="admin-at-due"
                        checked={settings.admin_overdue_reminder_at_due ?? true}
                        onChange={(e) => setSettings({ ...settings, admin_overdue_reminder_at_due: e.target.checked })}
                      />
                      <Label htmlFor="admin-at-due" className="text-sm">Alert on the overdue date</Label>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                        Repeat every (days)
                      </Label>
                      <Input
                        data-testid="admin-reminder-interval-input"
                        type="number"
                        min="1"
                        max="90"
                        value={settings.admin_overdue_reminder_interval_days ?? 7}
                        onChange={(e) => setSettings({ ...settings, admin_overdue_reminder_interval_days: parseInt(e.target.value) || 7 })}
                        className="mt-1.5 w-32"
                      />
                      <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>0 = no repeat (alert at overdue date only)</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <Button
                  data-testid="save-settings-button"
                  onClick={save}
                  disabled={saving}
                  style={{ background: "#292524", color: "#fff" }}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </div>

            {/* Message notification timing */}
            <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Message Notifications</p>
              <p className="text-xs" style={{ color: "#78716C" }}>
                When a message is sent, emails are delayed to avoid interrupting active users.
                Two notifications are sent if there is no reply:
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                    Stage 1 — notify offline users after (seconds)
                  </Label>
                  <p className="text-xs mt-0.5 mb-1.5" style={{ color: "#A8A29E" }}>
                    Users who are online are skipped at this stage. Default: 120s (2 min)
                  </p>
                  <Input
                    data-testid="grace-period-input"
                    type="number"
                    min="30"
                    max="3600"
                    value={settings.message_grace_period_seconds ?? 120}
                    onChange={(e) => setSettings({ ...settings, message_grace_period_seconds: parseInt(e.target.value) || 120 })}
                    className="w-32"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                    Stage 2 — remind everyone after (seconds)
                  </Label>
                  <p className="text-xs mt-0.5 mb-1.5" style={{ color: "#A8A29E" }}>
                    Catches users who were online but missed the message. Default: 1800s (30 min)
                  </p>
                  <Input
                    data-testid="final-grace-period-input"
                    type="number"
                    min="300"
                    max="86400"
                    value={settings.message_final_grace_period_seconds ?? 1800}
                    onChange={(e) => setSettings({ ...settings, message_final_grace_period_seconds: parseInt(e.target.value) || 1800 })}
                    className="w-32"
                  />
                </div>
              </div>

              <div className="pt-2">
                <Button
                  data-testid="save-settings-button-2"
                  onClick={save}
                  disabled={saving}
                  style={{ background: "#292524", color: "#fff" }}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </div>
            </>
          )}
        </div>

        {/* Email Templates Editor */}
        <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Email Templates</p>
          <p className="text-xs" style={{ color: "#78716C" }}>
            Customise the subject and body of each outbound email. Leave fields blank to use the built-in defaults.
          </p>
          <EmailTemplatesEditor
            currentSettings={settings}
            onSave={save}
            saving={saving}
          />
        </div>
      </div>
    </Layout>
  );
}
